import * as React from 'react';
import routes from '../routes';
import { PageViewTrackingWrapper } from '../helpers/PageViewTrackingWrapper';
import {
    AppRouteComponentMatchParams,
    OrgRouteComponentMatchParams,
    ReleaseRouteComponentMatchParams,
    ReleaseStepRouteComponentMatchParams,
    StepRunRouteComponentMatchParams,
    StepRunsSummaryRouteComponentMatchParams,
} from '../typescriptHelpers';
import { Redirect, Route, RouteComponentProps, Switch } from 'react-router-dom';
import AppSettingsContainer from './AppSettingsContainer';
import ReleaseOverviewContainer from './releaseOverview/ReleaseOverviewContainer';
import FeatureFlagsContainer from './featureFlags/FeatureFlags';
import { MonitoringContainer } from './monitoring/MonitoringContainer';
import { AppBuildsContainer, SharedWithMeBuildsContainer } from './buildDistribution/AppBuildsContainer';
import { AppHomeComponent } from './AppHomeComponent';
import {
    ValidatedAndLoadedReleaseStepWrapper,
    ValidatedAndLoadedReleaseWrapper,
    ValidatedAndLoadedStepRunsSummaryWrapper,
    ValidatedAppRouteWrapper,
    ValidatedOrgRouteWrapper,
} from './ValidatedRouteComponents';
import OrgSettings from './orgSettings/OrgSettings';
import UserSettingsComponent from './userSettings/UserSettingsComponent';
import NoAppsComponent from './dashboard/NoAppsComponent';
import { AppHomeGetStartedComponent } from './onboarding/AppHomeGetStartedComponent';
import { AutomationsToolboxContainer } from './automationsToolbox/AutomationsToolboxContainer';
import { MobileAddTestDevice } from './mobileAddTestDevice/MobileAddTestDevice';
import { SetupStepsContainer } from './setup/SetupStepsContainer';
import Layout from 'antd/lib/layout/layout';
import RestrictedAppComponent from './dashboard/RestrictedAppComponent';
import CommandModal from './commandk/CommandModal';
import styles from './styles/dashboardStyles';
import { ReleaseStepOrStepRunContainer } from './StepRunContainer';
import ReleaseCalendarContainer from './ReleaseCalendar';
import AppReleaseCalendar from '@/components/schedule/AppReleaseCalendar';
import TriagePage from '@/components/triage/TriagePage';
import IosDependenciesPage from '@/components/iosDependencies/IosDependenciesPage';
import TimelineEventsContainer from './timelineEvents/TimelineEventsContainer';
import AppTimelineContainer from './timelineEvents/AppTimelineContainer';
import { StepRunsSummary } from './stepRunSummaries/StepRunsSummary';
import { OrgOverviewRoute } from './orgOverview/OrgOverviewRoute';

export interface DashboardRoutesProps {
    orgId?: string;
    appId?: string;
    hasApps: boolean;
    isRestrictedApp: boolean;
}

export const DashboardRoutes: React.FC<DashboardRoutesProps> = ({ orgId, appId, hasApps, isRestrictedApp }) => {
    return (
        <>
            <Switch>
                <Route
                    path={[routes.setupSteps, routes.setup]}
                    render={(routeProps: RouteComponentProps) => {
                        const appRouteProps = routeProps as RouteComponentProps<AppRouteComponentMatchParams>;
                        return (
                            <ValidatedAppRouteWrapper {...appRouteProps} showNotFoundComponent>
                                {(validatedAppComponentProps) => (
                                    <PageViewTrackingWrapper pageTitle={'Setup'}>
                                        <SetupStepsContainer {...validatedAppComponentProps} />
                                    </PageViewTrackingWrapper>
                                )}
                            </ValidatedAppRouteWrapper>
                        );
                    }}
                />
                <Route
                    exact
                    path={routes.mobileAddTestDevice}
                    render={(routeProps: RouteComponentProps) => {
                        const appRouteProps = routeProps as RouteComponentProps<AppRouteComponentMatchParams>;
                        return (
                            <ValidatedAppRouteWrapper {...appRouteProps} showNotFoundComponent>
                                {(validatedAppComponentProps) => (
                                    <PageViewTrackingWrapper pageTitle={'Mobile Add Test Device'}>
                                        <MobileAddTestDevice {...validatedAppComponentProps} />
                                    </PageViewTrackingWrapper>
                                )}
                            </ValidatedAppRouteWrapper>
                        );
                    }}
                />
                <Route
                    path={routes.automationsToolbox}
                    render={(routeProps: RouteComponentProps) => {
                        const appRouteProps = routeProps as RouteComponentProps<AppRouteComponentMatchParams>;
                        return (
                            <ValidatedAppRouteWrapper {...appRouteProps} showNotFoundComponent>
                                {(validatedAppComponentProps) => (
                                    <PageViewTrackingWrapper pageTitle={'Automations Toolbox'}>
                                        <AutomationsToolboxContainer {...validatedAppComponentProps} />
                                    </PageViewTrackingWrapper>
                                )}
                            </ValidatedAppRouteWrapper>
                        );
                    }}
                />
                <Route
                    path={routes.appHomeGetStarted}
                    render={(routeProps: RouteComponentProps) => {
                        const appRouteProps = routeProps as RouteComponentProps<AppRouteComponentMatchParams>;
                        return (
                            <ValidatedAppRouteWrapper {...appRouteProps} showNotFoundComponent>
                                {(validatedAppComponentProps) => (
                                    <PageViewTrackingWrapper pageTitle={'App Home Get Started'}>
                                        <AppHomeGetStartedComponent {...validatedAppComponentProps} />
                                    </PageViewTrackingWrapper>
                                )}
                            </ValidatedAppRouteWrapper>
                        );
                    }}
                />
                <div
                    style={{
                        ...styles.containerStyle,
                    }}
                >
                    {isRestrictedApp && orgId && appId ?
                        <Layout>
                            <RestrictedAppComponent orgId={orgId} appId={appId} />
                        </Layout>
                    :   <Layout>
                            <Switch>
                                <Route
                                    path={[routes.stepRunsSummary]}
                                    render={(routeProps: RouteComponentProps) => {
                                        const stepRunsSummaryRouteProps =
                                            routeProps as RouteComponentProps<StepRunsSummaryRouteComponentMatchParams>;
                                        return (
                                            <ValidatedAndLoadedStepRunsSummaryWrapper
                                                {...stepRunsSummaryRouteProps}
                                                showNotFoundComponent
                                                showLoader
                                                withTracking={false}
                                            >
                                                {(validatedStepRunsSummaryRouteProps) => {
                                                    return <StepRunsSummary {...validatedStepRunsSummaryRouteProps} />;
                                                }}
                                            </ValidatedAndLoadedStepRunsSummaryWrapper>
                                        );
                                    }}
                                />
                                <Route
                                    path={[routes.releaseStepTab, routes.stepRunTab]}
                                    render={(routeProps: RouteComponentProps) => {
                                        const releaseRouteProps = routeProps as RouteComponentProps<
                                            ReleaseStepRouteComponentMatchParams & StepRunRouteComponentMatchParams
                                        >;
                                        return (
                                            <ValidatedAndLoadedReleaseStepWrapper
                                                {...releaseRouteProps}
                                                showNotFoundComponent
                                                showLoader
                                                withTracking={false}
                                            >
                                                {(validatedReleaseRouteProps) => {
                                                    return (
                                                        <ReleaseStepOrStepRunContainer
                                                            {...validatedReleaseRouteProps}
                                                        />
                                                    );
                                                }}
                                            </ValidatedAndLoadedReleaseStepWrapper>
                                        );
                                    }}
                                />
                                <Route
                                    path={[routes.releaseStep, routes.stepRun]}
                                    render={(routeProps: RouteComponentProps) => {
                                        const releaseRouteProps = routeProps as RouteComponentProps<
                                            ReleaseStepRouteComponentMatchParams & StepRunRouteComponentMatchParams
                                        >;
                                        return (
                                            <ValidatedAndLoadedReleaseStepWrapper
                                                {...releaseRouteProps}
                                                showNotFoundComponent
                                                showLoader
                                                withTracking={false}
                                            >
                                                {(validatedReleaseRouteProps) => {
                                                    return (
                                                        <ReleaseStepOrStepRunContainer
                                                            {...validatedReleaseRouteProps}
                                                        />
                                                    );
                                                }}
                                            </ValidatedAndLoadedReleaseStepWrapper>
                                        );
                                    }}
                                />
                                <Route
                                    path={routes.releaseOverview}
                                    render={(routeProps: RouteComponentProps) => {
                                        const releaseRouteProps =
                                            routeProps as RouteComponentProps<ReleaseRouteComponentMatchParams>;
                                        return (
                                            <ValidatedAndLoadedReleaseWrapper
                                                {...releaseRouteProps}
                                                showNotFoundComponent
                                                showLoader
                                                withTracking={false}
                                            >
                                                {(validatedReleaseRouteProps) => {
                                                    return <ReleaseOverviewContainer {...validatedReleaseRouteProps} />;
                                                }}
                                            </ValidatedAndLoadedReleaseWrapper>
                                        );
                                    }}
                                />
                                <Route
                                    path={[routes.releaseTriageIssue, routes.releaseTriage]}
                                    render={(routeProps: RouteComponentProps) => {
                                        const releaseRouteProps =
                                            routeProps as RouteComponentProps<ReleaseRouteComponentMatchParams>;
                                        return (
                                            <ValidatedAndLoadedReleaseWrapper
                                                {...releaseRouteProps}
                                                showNotFoundComponent
                                                showLoader
                                                withTracking={false}
                                            >
                                                {(validatedReleaseRouteProps) => {
                                                    return (
                                                        <PageViewTrackingWrapper pageTitle={'Triage'}>
                                                            <TriagePage {...validatedReleaseRouteProps} />
                                                        </PageViewTrackingWrapper>
                                                    );
                                                }}
                                            </ValidatedAndLoadedReleaseWrapper>
                                        );
                                    }}
                                />
                                <Route
                                    path={routes.releaseIosDependencies}
                                    render={(routeProps: RouteComponentProps) => {
                                        const releaseRouteProps =
                                            routeProps as RouteComponentProps<ReleaseRouteComponentMatchParams>;
                                        return (
                                            <ValidatedAndLoadedReleaseWrapper
                                                {...releaseRouteProps}
                                                showNotFoundComponent
                                                showLoader
                                                withTracking={false}
                                            >
                                                {(validatedReleaseRouteProps) => {
                                                    return (
                                                        <PageViewTrackingWrapper pageTitle={'Dependencies'}>
                                                            <IosDependenciesPage {...validatedReleaseRouteProps} />
                                                        </PageViewTrackingWrapper>
                                                    );
                                                }}
                                            </ValidatedAndLoadedReleaseWrapper>
                                        );
                                    }}
                                />
                                <Route
                                    path={routes.releaseTimeline}
                                    render={(routeProps: RouteComponentProps) => {
                                        const releaseRouteProps =
                                            routeProps as RouteComponentProps<ReleaseRouteComponentMatchParams>;
                                        return (
                                            <ValidatedAndLoadedReleaseWrapper
                                                {...releaseRouteProps}
                                                showNotFoundComponent
                                                showLoader
                                                withTracking={false}
                                            >
                                                {(validatedReleaseRouteProps) => {
                                                    return <TimelineEventsContainer {...validatedReleaseRouteProps} />;
                                                }}
                                            </ValidatedAndLoadedReleaseWrapper>
                                        );
                                    }}
                                />
                                <Route
                                    path={routes.releaseCalendar}
                                    render={(routeProps: RouteComponentProps) => {
                                        const releaseRouteProps =
                                            routeProps as RouteComponentProps<ReleaseRouteComponentMatchParams>;
                                        return (
                                            <ValidatedAndLoadedReleaseWrapper
                                                {...releaseRouteProps}
                                                showNotFoundComponent
                                                showLoader
                                                withTracking={false}
                                            >
                                                {(validatedReleaseRouteProps) => {
                                                    return <ReleaseCalendarContainer {...validatedReleaseRouteProps} />;
                                                }}
                                            </ValidatedAndLoadedReleaseWrapper>
                                        );
                                    }}
                                />
                                <Route
                                    path={routes.monitoring}
                                    render={(routeProps: RouteComponentProps) => {
                                        const releaseRouteProps =
                                            routeProps as RouteComponentProps<ReleaseRouteComponentMatchParams>;
                                        return (
                                            <ValidatedAndLoadedReleaseWrapper
                                                {...releaseRouteProps}
                                                showNotFoundComponent
                                                showLoader
                                                withTracking={false}
                                            >
                                                {(validatedReleaseRouteProps) => {
                                                    return <MonitoringContainer {...validatedReleaseRouteProps} />;
                                                }}
                                            </ValidatedAndLoadedReleaseWrapper>
                                        );
                                    }}
                                />
                                <Route
                                    path={routes.featureFlags}
                                    render={(routeProps: RouteComponentProps) => {
                                        const releaseRouteProps =
                                            routeProps as RouteComponentProps<ReleaseRouteComponentMatchParams>;
                                        return (
                                            <ValidatedAndLoadedReleaseWrapper
                                                {...releaseRouteProps}
                                                showNotFoundComponent
                                                showLoader
                                                withTracking={false}
                                            >
                                                {(validatedReleaseRouteProps) => {
                                                    return <FeatureFlagsContainer {...validatedReleaseRouteProps} />;
                                                }}
                                            </ValidatedAndLoadedReleaseWrapper>
                                        );
                                    }}
                                />
                                <Redirect exact from={routes.release} to={routes.releaseOverview} />
                                <Route
                                    path={[routes.releases]}
                                    render={(routeProps: RouteComponentProps) => {
                                        const appRouteProps =
                                            routeProps as RouteComponentProps<AppRouteComponentMatchParams>;

                                        return (
                                            <ValidatedAppRouteWrapper {...appRouteProps} showNotFoundComponent>
                                                {(validatedAppComponentProps) => (
                                                    <PageViewTrackingWrapper pageTitle={'App Overview'}>
                                                        <AppHomeComponent {...validatedAppComponentProps} />
                                                    </PageViewTrackingWrapper>
                                                )}
                                            </ValidatedAppRouteWrapper>
                                        );
                                    }}
                                />
                                <Route
                                    path={[routes.appBuilds, routes.appBucket]}
                                    render={(routeProps: RouteComponentProps) => {
                                        const appRouteProps =
                                            routeProps as RouteComponentProps<AppRouteComponentMatchParams>;

                                        return (
                                            <ValidatedAppRouteWrapper {...appRouteProps} showNotFoundComponent>
                                                {(validatedAppComponentProps) => (
                                                    <PageViewTrackingWrapper pageTitle={'Builds'}>
                                                        <AppBuildsContainer {...validatedAppComponentProps} />
                                                    </PageViewTrackingWrapper>
                                                )}
                                            </ValidatedAppRouteWrapper>
                                        );
                                    }}
                                />
                                <Route
                                    path={[routes.appTimeline]}
                                    render={(routeProps: RouteComponentProps) => {
                                        const appRouteProps =
                                            routeProps as RouteComponentProps<AppRouteComponentMatchParams>;

                                        return (
                                            <ValidatedAppRouteWrapper {...appRouteProps} showNotFoundComponent>
                                                {(validatedAppComponentProps) => (
                                                    <PageViewTrackingWrapper pageTitle={'Timeline'}>
                                                        <AppTimelineContainer {...validatedAppComponentProps} />
                                                    </PageViewTrackingWrapper>
                                                )}
                                            </ValidatedAppRouteWrapper>
                                        );
                                    }}
                                />
                                <Route
                                    path={[routes.appReleaseCalendar]}
                                    render={(routeProps: RouteComponentProps) => {
                                        const appRouteProps =
                                            routeProps as RouteComponentProps<AppRouteComponentMatchParams>;

                                        return (
                                            <ValidatedAppRouteWrapper {...appRouteProps} showNotFoundComponent>
                                                {(validatedAppComponentProps) => (
                                                    <PageViewTrackingWrapper pageTitle={'Release Schedule'}>
                                                        <AppReleaseCalendar {...validatedAppComponentProps} />
                                                    </PageViewTrackingWrapper>
                                                )}
                                            </ValidatedAppRouteWrapper>
                                        );
                                    }}
                                />
                                <Route
                                    path={[routes.appTriageIssue, routes.appTriage]}
                                    render={(routeProps: RouteComponentProps) => {
                                        const appRouteProps =
                                            routeProps as RouteComponentProps<AppRouteComponentMatchParams>;

                                        return (
                                            <ValidatedAppRouteWrapper {...appRouteProps} showNotFoundComponent>
                                                {(validatedAppComponentProps) => (
                                                    <PageViewTrackingWrapper pageTitle={'Triage'}>
                                                        <TriagePage {...validatedAppComponentProps} />
                                                    </PageViewTrackingWrapper>
                                                )}
                                            </ValidatedAppRouteWrapper>
                                        );
                                    }}
                                />
                                <Route
                                    path={[routes.sharedWithMeBucket]}
                                    render={(routeProps: RouteComponentProps) => {
                                        const appRouteProps =
                                            routeProps as RouteComponentProps<AppRouteComponentMatchParams>;

                                        return (
                                            <ValidatedAppRouteWrapper {...appRouteProps} showNotFoundComponent>
                                                {(validatedAppComponentProps) => (
                                                    <PageViewTrackingWrapper pageTitle={'Shared with me'}>
                                                        <SharedWithMeBuildsContainer {...validatedAppComponentProps} />
                                                    </PageViewTrackingWrapper>
                                                )}
                                            </ValidatedAppRouteWrapper>
                                        );
                                    }}
                                />

                                <Route
                                    exact
                                    path={[routes.app]}
                                    render={(routeProps: RouteComponentProps) => {
                                        const appRouteProps =
                                            routeProps as RouteComponentProps<AppRouteComponentMatchParams>;

                                        return (
                                            <ValidatedAppRouteWrapper {...appRouteProps} showNotFoundComponent>
                                                {(validatedAppComponentProps) => (
                                                    <PageViewTrackingWrapper pageTitle={'App Home'}>
                                                        <AppHomeComponent {...validatedAppComponentProps} />
                                                    </PageViewTrackingWrapper>
                                                )}
                                            </ValidatedAppRouteWrapper>
                                        );
                                    }}
                                />

                                <Route
                                    path={routes.appSettings}
                                    render={(routeProps: RouteComponentProps) => {
                                        const appRouteProps =
                                            routeProps as RouteComponentProps<AppRouteComponentMatchParams>;

                                        return (
                                            <ValidatedAppRouteWrapper {...appRouteProps} showNotFoundComponent>
                                                {(validatedAppComponentProps) => (
                                                    <AppSettingsContainer {...validatedAppComponentProps} />
                                                )}
                                            </ValidatedAppRouteWrapper>
                                        );
                                    }}
                                />
                                <Route
                                    exact
                                    path={routes.org}
                                    render={(routeProps: RouteComponentProps) => {
                                        const orgRouteProps =
                                            routeProps as RouteComponentProps<OrgRouteComponentMatchParams>;

                                        return (
                                            <ValidatedOrgRouteWrapper {...orgRouteProps} showNotFoundComponent>
                                                {(validatedOrgComponentProps) => (
                                                    <PageViewTrackingWrapper pageTitle={'Org Overview'}>
                                                        <OrgOverviewRoute {...validatedOrgComponentProps} />
                                                    </PageViewTrackingWrapper>
                                                )}
                                            </ValidatedOrgRouteWrapper>
                                        );
                                    }}
                                />
                                <Route
                                    exact
                                    path={routes.user}
                                    render={() => {
                                        return (
                                            <PageViewTrackingWrapper pageTitle={'User Settings'}>
                                                <UserSettingsComponent />
                                            </PageViewTrackingWrapper>
                                        );
                                    }}
                                />
                                <Route
                                    exact
                                    path={routes.orgSettings}
                                    render={(routeProps: RouteComponentProps) => {
                                        const orgRouteProps =
                                            routeProps as RouteComponentProps<OrgRouteComponentMatchParams>;

                                        return (
                                            <ValidatedOrgRouteWrapper {...orgRouteProps} showNotFoundComponent>
                                                {(validatedOrgComponentProps) => (
                                                    <PageViewTrackingWrapper pageTitle={'Org Settings'}>
                                                        <OrgSettings {...validatedOrgComponentProps} />
                                                    </PageViewTrackingWrapper>
                                                )}
                                            </ValidatedOrgRouteWrapper>
                                        );
                                    }}
                                />
                                <Route exact path={[routes.dashboard, routes.home]}>
                                    {!hasApps && <NoAppsComponent />}
                                </Route>
                            </Switch>
                        </Layout>
                    }
                </div>
            </Switch>
            <CommandModal />
        </>
    );
};
